import type { ReactNode } from "react";

import { useProfile } from "@/context/ProfileContext";
import { useTodayGreeting } from "@/components/today/use-today-greeting";
import { firstName } from "@/lib/greeting";
import { lilacPageTitle, lilacSubtitle } from "@/lib/lilac-classes";
import { cn } from "@/lib/utils";

interface Props {
  /** What the chat is about ("your team", a member's name) for the subline. */
  subtitle: string;
  /** The composer, rendered as the centrepiece under the greeting. */
  composer: ReactNode;
  /** Suggestions rendered under the composer. */
  children?: ReactNode;
}

/**
 * The empty state of the full-page coach: a time-of-day greeting, the
 * composer front and centre, then the topic starters. Once the first message
 * is sent the chat falls back to the transcript-with-bottom-composer layout.
 */
export function ChatLanding({ subtitle, composer, children }: Props) {
  const { memberProfile } = useProfile();
  const { heading } = useTodayGreeting(firstName(memberProfile?.name));

  return (
    <div className="flex min-h-full w-full flex-col items-center justify-center gap-6 py-10">
      <div className="flex flex-col items-center gap-1 text-center">
        <h1 className={cn(lilacPageTitle, "sm:text-4xl")}>
          {heading}
        </h1>
        <p className={lilacSubtitle}>{subtitle}</p>
      </div>
      <div className="w-full">{composer}</div>
      {children}
    </div>
  );
}
